import { OfferingSwitchConfirmation } from 'legacy/lut/components/widgets/public/partner360/programTypeButtons/components/programTypeButtonsControl/components/offeringSwitchConfirmation/OfferingSwitchConfirmation'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getCockpitFiscalYearLabel } from '../../../../lib'
import { getOfferingTitleKey } from '../../../../lib/getOfferingTitleKey'
import { getSwitchableCockpitOfferings } from '../../../../lib/getSwitchableCockpitOfferings'
import { useCanSwitchCockpitOffering, useCockpitOfferingSwitch } from '../../../../model/hooks'
import {
  selectCockpitFirstPartnerOfferingsCatalog,
  selectCockpitOffering,
  selectCockpitSwitchingOnOffering,
  selectEffectiveCockpitFYear,
} from '../../../../model/selectors'
import { PowerIcon } from '../../lib/PowerIcon'

import styles from './CockpitPlanDeck.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planDeck

interface IOfferingSwitchedOffNoteProps {
  partnerId: string
}

/** In place of the plan's state while the offering in view is switched off: it says so, that the
 *  plan stays to view, and offers to switch it back on to a user who may. */
export const OfferingSwitchedOffNote = ({ partnerId }: IOfferingSwitchedOffNoteProps) => {
  const t = useSafetyTranslation()
  const selectedOffering = useSelector(selectCockpitOffering)
  const switchingOnOffering = useSelector(selectCockpitSwitchingOnOffering)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const offeringsCatalog = useSelector(selectCockpitFirstPartnerOfferingsCatalog)
  const offerings = useMemo(
    () => getSwitchableCockpitOfferings(offeringsCatalog),
    [offeringsCatalog]
  )
  const canSwitch = useCanSwitchCockpitOffering(offerings)
  const { isDistributor, pendingChange, requestChange, confirmChange, cancelChange } =
    useCockpitOfferingSwitch(partnerId)

  const offering = offerings.find(({ programType }) => programType === selectedOffering)

  if (!offering) {
    return null
  }

  return (
    <div className={styles.offNote}>
      <span className={styles.offMark}>
        <PowerIcon className={styles.offIcon} />
      </span>
      <span className={styles.offText}>
        <span className={styles.offTitle}>
          <Translation
            text={KEYS.switchedOff}
            replaceParams={[
              { key: '{offering}', value: t(getOfferingTitleKey(offering.programType)) },
              { key: '{year}', value: getCockpitFiscalYearLabel(fYear) },
            ]}
            span
          />
        </span>
        <span className={styles.offHint}>
          <Translation text={KEYS.switchedOffHint} span />
        </span>
      </span>

      {canSwitch(offering) && (
        <Button
          variant="contained"
          color="primary"
          disabled={switchingOnOffering !== null}
          onClick={() => requestChange({ offering, flag: true })}
        >
          <span className={styles.offButton}>
            <PowerIcon className={styles.offButtonIcon} />
            <Translation text={KEYS.switchOn} span />
          </span>
        </Button>
      )}

      <OfferingSwitchConfirmation
        isShow={pendingChange !== null}
        isDistributor={isDistributor}
        state={pendingChange}
        onConfirm={confirmChange}
        onCancel={cancelChange}
      />
    </div>
  )
}

OfferingSwitchedOffNote.displayName = 'OfferingSwitchedOffNote'
